import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import './WikiItemDetail.css';
import { McIcon } from '../components/CraftingGrid';
import api from '../API/Api';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4";

const STATUS_CONFIG = {
  active:    { label: "ACTIVE",      bg: "rgba(26,175,18,0.12)",  border: "#1aaf12", color: "#1aaf12" },
  upcoming:  { label: "UPCOMING",    bg: "rgba(198,118,26,0.12)", border: "#c6761a", color: "#c6761a" },
  ended:     { label: "ENDED",       bg: "rgba(122,122,122,0.12)",border: "#7a7a7a", color: "#7a7a7a" },
  disabled:  { label: "DISABLED",    bg: "rgba(180,40,40,0.12)",  border: "#b42828", color: "#b42828" },
  comingsoon:{ label: "COMING SOON", bg: "rgba(200,184,0,0.12)",  border: "#c8b800", color: "#c8b800" },
  changed:   { label: "CHANGED",     bg: "rgba(224,123,26,0.12)", border: "#e07b1a", color: "#e07b1a" },
};

function StatusBadge({ type }) {
  const cfg = STATUS_CONFIG[type] || STATUS_CONFIG.upcoming;
  return (
    <span style={{
      display: "inline-block", fontSize: "0.65rem", fontWeight: 700,
      letterSpacing: "0.08em", textTransform: "uppercase",
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "0.25rem",
      padding: "0.2rem 0.55rem",
    }}>
      {cfg.label}
    </span>
  );
}

function InfoCard({ icon, title, status, wikiLink, children }) {
  const titleEl = wikiLink ? (
    <Link to={wikiLink} style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--text)", textDecoration: "none" }}>
      {title} ↗
    </Link>
  ) : (
    <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>{title}</span>
  );

  return (
    <div className="wiki-detail-box" style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
          <McIcon materialKey={icon} size={22} />
          {titleEl}
        </div>
        <StatusBadge type={status} />
      </div>
      {children}
    </div>
  );
}

export default function WikiSeasonInfo() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('getwikiseason').then(res => {
      if (res.isSuccess) {
        setConfig(res.data.config);
        setFeatures(res.data.features ?? []);
      }
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="wiki-detail-root" style={{ paddingTop: "8rem", textAlign: "center", color: "var(--text-muted)" }}>
        Loading…
      </div>
    );
  }

  if (!config) {
    return (
      <div className="wiki-detail-root" style={{ paddingTop: "8rem", textAlign: "center", color: "var(--text-muted)" }}>
        Season info not available.
      </div>
    );
  }

  const disabledFeatures  = features.filter(f => f.category === "disabled");
  const mechanicFeatures  = features.filter(f => f.category === "mechanic");
  const seasonColor       = config.color || "#c6761a";

  const supplyStatusCfg   = STATUS_CONFIG[config.supply_drop_status] ?? STATUS_CONFIG.active;

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': seasonColor }}>
              <McIcon materialKey="beacon" size={56} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{ color: seasonColor }}>
                {config.name}
              </h1>
              <div className="wiki-detail-item-type" style={{ color: seasonColor }}>
                Season {config.season_number}{config.theme ? ` · ${config.theme}` : ""}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">

          {/* ── Main ── */}
          <StaggerItem className="wiki-detail-main">

            {/* Season Status Banner */}
            <div>
              <span className="wiki-detail-label">Current Season</span>
              <div style={{
                background: `${seasonColor}11`,
                border: `1px solid ${seasonColor}55`,
                borderRadius: "0.75rem",
                padding: "1.5rem",
                display: "flex", flexDirection: "column", gap: "1rem",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: "0.6rem",
                    background: `${seasonColor}25`, border: `1px solid ${seasonColor}`,
                    borderRadius: "2rem", padding: "0.4rem 1rem",
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: seasonColor, boxShadow: `0 0 6px ${seasonColor}`,
                      display: "inline-block",
                    }} />
                    <span style={{ color: seasonColor, fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {STATUS_CONFIG[config.status]?.label ?? config.status.toUpperCase()}
                    </span>
                  </div>
                  {config.start_date && (
                    <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                      Launch: {new Date(config.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {config.description && (
                  <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.7, fontSize: "0.95rem" }}>
                    {config.description}
                  </p>
                )}

                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem",
                  paddingTop: "1rem", borderTop: `1px solid ${seasonColor}33`,
                }}>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.25rem" }}>Season Start</div>
                    <div style={{ fontWeight: 600 }}>{config.start_date ? new Date(config.start_date).toLocaleDateString() : "TBA"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.25rem" }}>Season End</div>
                    <div style={{ fontWeight: 600 }}>{config.end_date ? new Date(config.end_date).toLocaleDateString() : "Unknown"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Disabled Content */}
            {disabledFeatures.length > 0 && (
              <div>
                <span className="wiki-detail-label">Disabled Content</span>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {disabledFeatures.map(f => (
                    <InfoCard key={f.id} icon={f.icon} title={f.title} status={f.status} wikiLink={f.wiki_link || null}>
                      {f.description && (
                        <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                          {f.description}
                        </div>
                      )}
                    </InfoCard>
                  ))}
                </div>
              </div>
            )}

            {/* Supply Drops */}
            <div>
              <span className="wiki-detail-label">Supply Drops</span>
              <div style={{
                background: "rgba(200,184,0,0.05)", border: "1px solid rgba(200,184,0,0.25)",
                borderRadius: "0.75rem", padding: "1.25rem",
                display: "flex", flexDirection: "column", gap: "0.75rem",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <McIcon materialKey="chest" size={28} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "1rem" }}>Supply Drop Events</div>
                    <StatusBadge type={config.supply_drop_status} />
                  </div>
                </div>
                {config.supply_drop_description && (
                  <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.7, fontSize: "0.9rem" }}>
                    {config.supply_drop_description}
                  </p>
                )}
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "0.5rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(200,184,0,0.15)",
                }}>
                  {[
                    { icon: "clock",      label: config.supply_drop_schedule || "Scheduled events" },
                    { icon: "map",        label: "Random locations" },
                    { icon: "gold_ingot", label: "Rare exclusive loot" },
                    { icon: "bell",       label: "Server announcements" },
                  ].map((point, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      <McIcon materialKey={point.icon} size={16} />
                      {point.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mechanic Changes */}
            {mechanicFeatures.length > 0 && (
              <div>
                <span className="wiki-detail-label">Mechanic Changes</span>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {mechanicFeatures.map(f => (
                    <div key={f.id} className="wiki-detail-box" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                          <McIcon materialKey={f.icon} size={22} />
                          {f.wiki_link ? (
                            <Link to={f.wiki_link} style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text)", textDecoration: "none" }}>
                              {f.title} ↗
                            </Link>
                          ) : (
                            <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{f.title}</span>
                          )}
                        </div>
                        <StatusBadge type={f.status} />
                      </div>
                      {f.description && (
                        <p style={{ margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                          {f.description}
                        </p>
                      )}
                      {f.bullets?.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                          {f.bullets.map((b, i) => {
                            const isLast = i === f.bullets.length - 1;
                            const row = (
                              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.85rem", color: "var(--text-secondary)", background: "rgba(255,255,255,0.03)", borderRadius: "0.4rem", padding: "0.5rem 0.75rem" }}>
                                <McIcon materialKey={b.icon || "chest"} size={16} />
                                {b.text}
                              </div>
                            );
                            // Last bullet on the Enchanted Shears card gets a link wrapper if wiki_link exists
                            if (f.wiki_link && isLast && f.bullets.length === 1) {
                              return (
                                <Link key={i} to={f.wiki_link} style={{ textDecoration: "none" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.85rem", color: "var(--text-secondary)", background: "rgba(255,255,255,0.03)", borderRadius: "0.4rem", padding: "0.5rem 0.75rem" }}>
                                    <McIcon materialKey={b.icon || "chest"} size={16} />
                                    <span>Use <strong style={{ color: "var(--teal)" }}>{f.title} ↗</strong> to obtain golden apples</span>
                                  </div>
                                </Link>
                              );
                            }
                            return <div key={i}>{row}</div>;
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </StaggerItem>

          {/* ── Sidebar ── */}
          <StaggerItem className="wiki-detail-sidebar">

            <div>
              <span className="wiki-detail-label">Upcoming</span>
              <div className="wiki-detail-box">
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#c8b800", display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, color: "#c8b800", fontSize: "0.9rem" }}>Season 2</span>
                </div>
                <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>To be continued</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.4rem" }}>Start: Unknown</div>
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">Past Seasons</span>
              <div className="wiki-detail-box" style={{ textAlign: "center", padding: "2rem 1rem" }}>
                <McIcon materialKey="barrier" size={32} />
                <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "0.75rem", lineHeight: 1.6 }}>
                  No past seasons yet.
                  <br />
                  <span style={{ color: seasonColor }}>Season 1</span> is upcoming.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <Link to="/wiki/items"   style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ Browse All Items</Link>
              <Link to="/wiki/mobs"    style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ Browse All Mobs</Link>
              <Link to="/wiki/updates" style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ View Updates</Link>
              <Link to="/"             style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ Back to Home</Link>
            </div>

          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
